import { lt, useDialog, useFeatureFlag } from '@mews/framework';
import { ContentCard, DefaultErrorBoundary, EnterpriseLinkButton } from '@mews-commander/ui';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    ActionToolbar,
    ActionToolbarButton,
    IconName,
    PAGINATION_SIZES,
    Table,
    TableProvider,
    usePaginatedData,
    useRowSelection,
    useTableState,
} from '@optimus-web/core';
import { OrderItem } from '@mews-commander/types';
import { OrderItemType } from '@mews-commander/data-access';
import { useIsMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/enterprise';
import { FeatureFlag } from '@mews-commander/enums';
import { getSortedReservationsAdditionalExpenses } from '../../../selectors/orderItems';
import { pushUrlToHistory } from '../../../../app/actions/common';
import { simpleColumns, TableMeta } from './TableDefinition';
import { useOrderItemsCancelMutation } from './useOrderItemsCancelMutation';
import { CancelDepositWarningDialog } from './CancelDepositWarningDialog';

interface AdditionalExpensesTableProps {
    refetchGroup: () => void;
    reservationId: string;
    canReservationAcceptNewDeposit: boolean;
    serviceId: string;
    groupId: string;
}

export const AdditionalExpensesTable: React.FC<AdditionalExpensesTableProps> = ({
    refetchGroup,
    reservationId,
    canReservationAcceptNewDeposit,
    serviceId,
    groupId,
}) => {
    const additionalExpenses = useCommanderSelector((state) =>
        getSortedReservationsAdditionalExpenses(state, { serviceOrderIds: [reservationId] })
    );
    const isMultiTaxEnvironment = useIsMultiTaxEnvironment();
    const { isPending, mutate } = useOrderItemsCancelMutation();
    const onCancelExpenses = (additionalExpenseIds: string[], onSuccessCallback: () => void) => {
        return mutate(
            {
                orderItemIds: additionalExpenseIds,
            },
            {
                onSuccess: () => {
                    refetchGroup();
                    onSuccessCallback();
                },
            }
        );
    };

    const openNewDepositModal = () => pushUrlToHistory();

    return (
        <AdditionalExpensesTableCore
            isCancellingExpenses={isPending}
            additionalExpenses={additionalExpenses || []}
            isMultiTaxEnvironment={isMultiTaxEnvironment}
            onCancelExpenses={onCancelExpenses}
            openNewDepositModal={openNewDepositModal}
            canReservationAcceptNewDeposit={canReservationAcceptNewDeposit}
            serviceId={serviceId}
            groupId={groupId}
            reservationId={reservationId}
        />
    );
};

interface AdditionalExpensesTableCoreProps {
    isCancellingExpenses: boolean;
    additionalExpenses: OrderItem[];
    isMultiTaxEnvironment: boolean;
    onCancelExpenses: (additionalExpenseIds: string[], onSuccessCallback: () => void) => void;
    openNewDepositModal: () => void;
    canReservationAcceptNewDeposit: boolean;
    serviceId: string;
    groupId: string;
    reservationId: string;
}

const AdditionalExpensesTableCore: React.FC<AdditionalExpensesTableCoreProps> = ({
    isMultiTaxEnvironment,
    isCancellingExpenses,
    onCancelExpenses,
    openNewDepositModal,
    canReservationAcceptNewDeposit,
    additionalExpenses,
    serviceId,
    groupId,
    reservationId,
}) => {
    const [tableState, setTableState] = useTableState<OrderItem>({
        pagination: {
            show: true,
            pageSize: PAGINATION_SIZES[25],
        },
    });

    const depositCancellationImprovementEnabled = useFeatureFlag(FeatureFlag.depositCancellationImprovement);

    const [dialogDepositProps, toggleDepositDialog] = useDialog();

    const { rowSelection, setRowSelection, selectedRowsCount, resetRowSelection, hasRowsSelected, selectedRowsIds } =
        useRowSelection();

    const onCancel = () => {
        const containsDeposit = additionalExpenses.some(
            (expense) => selectedRowsIds.includes(expense.id) && expense.data.discriminator === OrderItemType.Deposit
        );

        const containsRebatedDeposit = additionalExpenses.some((expense) => {
            // limit to only selected rows
            if (!selectedRowsIds.includes(expense.id)) return false;

            // only check for OrderItemType.AdditionalExpenseRebate
            const originalItemId =
                expense.data.discriminator === OrderItemType.AdditionalExpenseRebate
                    ? expense.data.value.originalItemId
                    : null;

            // if expense is not OrderItemType.AdditionalExpenseRebate, we ignore the row
            if (!originalItemId) return false;

            // check if the original item is a OrderItemType.Deposit
            return additionalExpenses.some(
                (innerExpense) =>
                    innerExpense.id === originalItemId && innerExpense.data.discriminator === OrderItemType.Deposit
            );
        });

        if ((containsDeposit || containsRebatedDeposit) && depositCancellationImprovementEnabled) {
            toggleDepositDialog();
        } else {
            onCancelExpenses(selectedRowsIds, resetRowSelection);
        }
    };

    const canOrderItemBeCancelled = (orderItem: OrderItem) => {
        return depositCancellationImprovementEnabled ? !!orderItem.isCancelable : true;
    };

    const { data: additionalExpensesSlice, totalRowCount } = usePaginatedData(
        additionalExpenses,
        tableState.pagination
    );

    return (
        <DefaultErrorBoundary>
            <ContentCard
                title={lt('AdditionalExpensePlural')}
                actions={
                    canReservationAcceptNewDeposit && (
                        <EnterpriseLinkButton
                            data-test-new-deposit-button
                            params={{ reservationId }}
                            to="Reservation/CreateDeposit/$reservationId"
                            onClick={openNewDepositModal}
                        >
                            {lt('NewDeposit')}
                        </EnterpriseLinkButton>
                    )
                }
                data-test-items-addition-expense-table
            >
                <TableProvider columns={simpleColumns} state={tableState} onStateChange={setTableState}>
                    {hasRowsSelected && (
                        <ActionToolbar
                            leftSide={lt('ItemsSelected', { count: selectedRowsCount })}
                            rightSide={
                                <>
                                    <ActionToolbarButton
                                        icon={IconName.Delete}
                                        onClick={() => {
                                            onCancel();
                                        }}
                                    >
                                        {lt('Delete')}
                                    </ActionToolbarButton>
                                </>
                            }
                        />
                    )}
                    <Table
                        data-test-id="additional-expenses-table"
                        uniqueRowId="id"
                        rowSelection={{
                            rowSelection,
                            onRowSelectionChange: setRowSelection,
                            enableRowSelection: (row) =>
                                !row.original.isCanceled && canOrderItemBeCancelled(row.original),
                        }}
                        amountOfRows={totalRowCount}
                        data={additionalExpensesSlice}
                        loading={isCancellingExpenses}
                        meta={
                            {
                                isMultiTaxEnvironment,
                                serviceId,
                                groupId,
                            } satisfies TableMeta
                        }
                    />
                </TableProvider>
            </ContentCard>
            <CancelDepositWarningDialog
                {...dialogDepositProps}
                onSuccess={() => {
                    onCancelExpenses(selectedRowsIds, resetRowSelection);
                }}
            />
        </DefaultErrorBoundary>
    );
};
